import React, { useEffect, useMemo, useState } from "react";
import Api from "../../../../store/api/Api";
import Button from "../../../ui/common/Button";
import JsonTextArea from "../../../ui/common/JsonTextArea";
import "./DatalayerConfigurationModal.scss";

interface DatalayerConfigurationModalProps {
  brandId: string;
  onClose: () => void;
}

const EXAMPLE_CONFIGURATION = [
  {
    window: {
      mapping: [
        { inputField: "brandId", outputField: "brandId" },
        { inputField: "window._ain.access_level", outputField: "articleAccess" },
        { inputField: "window._ain.postid", outputField: "relationId" },
        { inputField: "window._ain.pubdate", outputField: "timestamp" },
        { inputField: "window._ain.sections", outputField: "section" },
        { inputField: "window._ain.authors", outputField: "author" },
        { inputField: "window._ain.tags", outputField: "tags" },
        { inputField: "window._ain.url", outputField: "uri" },
        { inputField: "window._ain.title", outputField: "title" },
        { inputField: "window._ain.id", outputField: "region" },
      ],
    },
  },
];

const DatalayerConfigurationModal = ({ brandId, onClose }: DatalayerConfigurationModalProps) => {
  const api = useMemo(() => new Api(), []);
  const [configuration, setConfiguration] = useState("[]");
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchConfiguration = async () => {
      try {
        const datalayer = await api.getDatalayerConfig(brandId);
        if (datalayer && Object.keys(datalayer).length === 0) {
          setConfiguration("");
          setExists(false);
        } else {
          setConfiguration(JSON.stringify(datalayer.config, null, 2));
          setExists(true);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          setError("Failed to load the datalayer configuration.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConfiguration();
  }, [api, brandId]);

  const validate = () => {
    try {
      const parsed = JSON.parse(configuration);
      if (!Array.isArray(parsed)) {
        setError("The datalayer configuration must be a JSON array.");
        return null;
      }
      return parsed;
    } catch {
      setError("Please enter valid JSON.");
      return null;
    }
  };

  const handleSave = async () => {
    const config = validate();
    if (!config) return;

    setError("");
    setSaving(true);
    try {
      if (exists) {
        await api.updateDatalayerConfig(brandId, config);
      } else {
        await api.createDatalayerConfig(brandId, config);
      }
      onClose();
    } catch {
      setError("Failed to save the datalayer configuration.");
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete the datalayer configuration for ${brandId}? This cannot be undone.`)) return;

    setError("");
    setSaving(true);
    try {
      await api.deleteDatalayerConfig(brandId);
      onClose();
    } catch {
      setError("Failed to delete the datalayer configuration.");
      setSaving(false);
    }
  };

  return (
    <div className="datalayer-configuration-modal">
      <h3>Datalayer Configuration for {brandId}</h3>
      {loading ? (
        <p>Loading configuration...</p>
      ) : (
        <>
          <JsonTextArea
            label="Configuration"
            ariaLabel="Datalayer configuration"
            rows={24}
            value={configuration}
            onChange={(value) => {
              setConfiguration(value);
              setError("");
            }}
          />
          {!exists && (
            <Button
              className="example-configuration-button"
              onClick={() => setConfiguration(JSON.stringify(EXAMPLE_CONFIGURATION, null, 2))}
            >
              Add example configuration
            </Button>
          )}
        </>
      )}
      {error && <div className="error-message">{error}</div>}
      <div className="sub-modal-actions">
        {exists && <Button className="delete-button" variant="danger" disabled={saving} onClick={handleDelete}>Delete</Button>}
        <Button variant="danger" onClick={onClose}>Cancel</Button>
        <Button variant="success" disabled={loading || saving} onClick={handleSave}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default DatalayerConfigurationModal;
